import { Badge, Loading, Panel, Typography } from "@/components/atoms";
import { getDisabledPlanInfo } from "@/redux/apis/subscription/subscriptionLimits";
import { useGetSubscriptionPlansQuery } from "@/redux/apis/subscription/subscriptionPlansApi";
import { clsx } from "@/utils";
import { calculateTotalMonthlyCost, formatCentsToDollars } from "./helpers/pricingCalculations";

interface EmbedPricingResultsPanelProps {
  monthlyBookings: number;
}

export const EmbedPricingResultsPanel = ({ monthlyBookings }: EmbedPricingResultsPanelProps) => {
  const { data: plansData, isLoading } = useGetSubscriptionPlansQuery();

  const plans = plansData?.subscriptions ?? [];

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Panel className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {plans.map((plan) => {
          const totalMonthlyCost = calculateTotalMonthlyCost(plan.subscriptionFee, plan.platformFeePerRide, monthlyBookings);
          const disabledPlanInfo = getDisabledPlanInfo(plan.slug);
          const isPlanDisabled = disabledPlanInfo.disabled;

          return (
            <div
              key={plan.subscriptionId}
              className={clsx(
                "flex flex-col rounded-lg border bg-neutral-surface-gray",
                "px-4 py-4",
                "border-neutral-mid-gray",
                isPlanDisabled && "opacity-50"
              )}
            >
              {/* Logo and Plan Name */}
              <div className="mb-2.5 flex items-center gap-3">
                {plan.iconSvg && (
                  <div
                    className="flex h-6 w-6 flex-shrink-0 [&>svg]:h-full [&>svg]:w-full"
                    dangerouslySetInnerHTML={{ __html: plan.iconSvg }}
                  />
                )}
                <Typography variant="title" className="text-neutral-dark-gray">
                  {plan.subscriptionName}
                </Typography>
                {isPlanDisabled && <Badge type="disabled" message={disabledPlanInfo.reason || ""} size="sm" />}
              </div>

              {/* Total Monthly Cost */}
              <Typography variant="h1" className="mb-2 text-neutral-black">
                ${totalMonthlyCost.toFixed(2)} <span className="text-lg font-normal">/mo.</span>
              </Typography>

              {/* Per Trip Cost */}
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                Per Trip Cost: {formatCentsToDollars(plan.platformFeePerRide)} /trip
              </Typography>

              {/* Plan Base Fee */}
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                {plan.subscriptionName} Plan: {formatCentsToDollars(plan.subscriptionFee)} /month
              </Typography>
            </div>
          );
        })}
      </div>
    </Panel>
  );
};
